import React from 'react';
import { LogOut, Check, Building, Users, UserCheck, User, MapPin, MapPinPlus, Package, FileText, Shield, CheckCircle } from 'lucide-react';

// TaxSetu Logo - Infinity symbol
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const GSTPortalWrapper = ({ 
  children, 
  onLogout, 
  activeSection = 1, 
  completedSections = [], 
  creationDate,
  onSectionClick 
}) => {
  // Calculate profile percentage based on completed sections
  const profilePercentage = completedSections.length * 11; // ~11% per section (10 sections = ~100%)
  // Calculate due date (15 days from creation)
  const getDueDate = () => {
    const date = creationDate ? new Date(creationDate) : new Date();
    date.setDate(date.getDate() + 15);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getLastModified = () => {
    return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Section tabs for Part-B with icons
  const sections = [
    { id: 1, name: 'Business Details', Icon: Building },
    { id: 2, name: 'Promoter / Partners', Icon: Users },
    { id: 3, name: 'Authorized Signatory', Icon: UserCheck },
    { id: 4, name: 'Authorized Representative', Icon: User },
    { id: 5, name: 'Principal Place of Business', Icon: MapPin },
    { id: 6, name: 'Additional Places of Business', Icon: MapPinPlus },
    { id: 7, name: 'Goods and Services', Icon: Package },
    { id: 8, name: 'State Specific Information', Icon: FileText },
    { id: 9, name: 'Aadhaar Authentication', Icon: Shield },
    { id: 10, name: 'Verification', Icon: CheckCircle },
  ];

  // Check if section is completed
  const isSectionCompleted = (sectionId) => completedSections.includes(sectionId);
  
  // Check if section is active
  const isSectionActive = (sectionId) => activeSection === sectionId;

  // Get current section name for breadcrumb
  const currentSectionName = sections.find(s => s.id === activeSection)?.name || 'Business Details';

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Skip to Main Content Bar */}
      <div className="bg-[#0b1e3f] text-white text-xs py-1 px-4 flex justify-end items-center gap-3">
        <span className="text-gray-300 cursor-pointer hover:underline">Skip to Main Content</span>
        <span className="flex items-center gap-2 text-gray-300">
          <span className="w-5 h-5 bg-yellow-500 rounded text-black text-xs flex items-center justify-center font-bold">0</span>
          <span className="cursor-pointer">A<sup>+</sup></span>
          <span className="cursor-pointer">A<sup>-</sup></span>
        </span>
      </div>

      {/* Main Header */}
      <header className="bg-[#0b1e3f] text-white py-6 px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="h-14 flex-shrink-0">
              <img 
                src={TAXSETU_LOGO}
                alt="PractiQ"
                className="h-full object-contain"
              />
            </div>
            <div className="border-l border-white/30 pl-5">
              <h1 className="text-2xl font-normal tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Demo Goods and Services Tax
              </h1>
              <p className="text-base text-gray-300 mt-1">
                Practical Simulation - Not a Government Website - Only for Educational Purpose
              </p>
            </div>
          </div>
          <button 
            onClick={onLogout}
            className="text-white hover:text-gray-300 flex items-center gap-2 text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="bg-[#2a5298]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center">
            <button className="px-5 py-3 bg-[#f7941d] text-white font-medium text-sm">
              Dashboard
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Services ▼
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              GST Law
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Downloads ▼
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Search Taxpayer ▼
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              Help and Taxpayer Facilities
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              e-Invoice
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              News and Updates
            </button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b py-2 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-blue-700 hover:underline cursor-pointer">Dashboard</span>
            <span className="text-gray-400">&gt;</span>
            <span className="text-gray-600">{currentSectionName}</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-red-500">📍</span>
            <span className="text-blue-600">English</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Aadhaar Verification Notice */}
          <div className="bg-red-50 border border-red-200 rounded px-4 py-2 mb-4 flex items-center gap-2">
            <span className="text-red-500 text-lg">⚠️</span>
            <span className="text-red-600 text-sm">
              Mobile number/e-mail id linked with Aadhaar can be verified at{' '}
              <a href="#" className="text-blue-600 underline">http://...</a>
            </span>
          </div>

          {/* Application Info Box */}
          <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-gray-600 text-sm font-medium">Application Type</p>
                <p className="text-gray-800 font-semibold">New Registration</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Due Date to Complete</p>
                <p className="text-gray-800 font-semibold">{getDueDate()}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Last Modified</p>
                <p className="text-gray-800 font-semibold">{getLastModified()}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Profile</p>
                <p className="text-blue-600 font-semibold text-lg">{profilePercentage}%</p>
              </div>
            </div>
          </div>

          {/* Section Tabs (1-10) */}
          <div className="bg-white rounded-lg shadow-sm mb-6 overflow-x-auto">
            <div className="flex items-center border-b">
              {sections.map((section) => {
                const isCompleted = isSectionCompleted(section.id);
                const isActive = isSectionActive(section.id);
                const SectionIcon = section.Icon;
                
                return (
                  <div
                    key={section.id}
                    onClick={() => onSectionClick && onSectionClick(section.id)}
                    className={`flex flex-col items-center px-4 py-3 min-w-[100px] cursor-pointer transition-all relative ${
                      isCompleted 
                        ? 'bg-[#2a5298]' 
                        : isActive 
                          ? 'bg-blue-50 border-b-2 border-blue-500' 
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Checkmark for completed sections */}
                    {isCompleted && (
                      <div className="absolute top-1 right-1">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                      isCompleted 
                        ? 'bg-white/20' 
                        : isActive 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isCompleted ? (
                        <SectionIcon className="w-5 h-5 text-white" />
                      ) : (
                        <span className={isActive ? 'text-white font-medium' : ''}>{section.id}</span>
                      )}
                    </div>
                    <span className={`text-xs text-center ${
                      isCompleted 
                        ? 'text-white font-medium' 
                        : isActive 
                          ? 'text-blue-600 font-medium' 
                          : 'text-gray-500'
                    }`}>
                      {section.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content Area - Part B Form */}
          <div className="bg-white rounded-lg shadow-sm">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a5298] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs text-white/80 mb-2">
            This is a Practice Simulation Portal by PractiQ for educational purposes only. 
            Not affiliated with or endorsed by any Government entity.
          </p>
          <div className="flex flex-wrap items-center justify-between text-xs text-white/60">
            <span>© 2024-25 PractiQ - EdTech Platform</span>
            <span>Site Last Updated on {new Date().toLocaleDateString('en-GB')}</span>
            <span>For Practice Purpose Only</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GSTPortalWrapper;
